import { useState } from 'react'
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ShieldCheck,
  Crown,
  Clock,
  FileText,
  Download,
  ClipboardList,
  Hash,
  MessageSquare,
  GitBranch,
} from 'lucide-react'
import { Button, Input, Badge } from '@/components/ui'
import type { ApprovalRequest } from '../../types/admin.types'
import { useAdminSubscriptions } from '../../hooks/use-admin'

export interface ApprovalDialogProps {
  isOpen: boolean
  onClose: () => void
  request: ApprovalRequest | null
  mode: 'approve' | 'reject'
  onSubmit: (id: number, notes?: string, riskLevel?: 'LOW' | 'MEDIUM' | 'HIGH', planAssignment?: any) => Promise<void>
  isPending: boolean
}

/** Converts a camelCase/snake_case key to a readable title */
function humanize(key: string): string {
  return key
    .replace(/^doc_/, '')            // strip leading doc_
    .replace(/_/g, ' ')              // snake_case → spaces
    .replace(/([A-Z])/g, ' $1')     // camelCase → spaces
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}

export function ApprovalDialog({
  isOpen,
  onClose,
  request,
  mode,
  onSubmit,
  isPending,
}: ApprovalDialogProps) {
  const [notes, setNotes] = useState('')
  const [riskLevel, setRiskLevel] = useState<'LOW' | 'MEDIUM' | 'HIGH'>('LOW')
  const [assignmentType, setAssignmentType] = useState<'DEFAULT' | 'SPECIFIC' | 'NONE' | 'REQUIRE_PAYMENT' | 'TRIAL'>('REQUIRE_PAYMENT')
  const [selectedPlanId, setSelectedPlanId] = useState<number | undefined>(undefined)
  const [customTrialDays, setCustomTrialDays] = useState<number>(14)

  const { data: subscriptionsData } = useAdminSubscriptions()
  const activePlans = subscriptionsData?.plans ?? []

  if (!isOpen || !request) return null

  const isApprove = mode === 'approve'
  const filteredPlans = activePlans.filter((p: any) => p.planType === request.requestType || !p.planType)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit(request.id, notes, riskLevel, {
      assignmentType,
      planId: selectedPlanId,
      customTrialDays,
    })
    setNotes('')
    onClose()
  }

  // Application data — already cleaned (no file objects)
  const appData: Record<string, any> = request.applicationData ?? {}
  const appDocs = request.uploadedDocuments ?? []
  const formVersion = request.formVersion ?? 1
  const remarks = request.remarks

  // Build a label map from formConfigSnapshot fields for human-readable field names
  const snapshotFields: { name: string; label: string }[] =
    request.formConfigSnapshot?.fields ?? []
  const getFieldLabel = (key: string): string =>
    snapshotFields.find((f) => f.name === key)?.label ?? humanize(key)

  // Only show scalar (non-object) entries — objects would be file uploads
  const appDataEntries = Object.entries(appData).filter(
    ([, v]) => v !== null && v !== undefined && v !== '' && typeof v !== 'object'
  )

  const hasApplicationContent = appDataEntries.length > 0 || appDocs.length > 0 || remarks

  // Shared select class for light+dark
  const selectCls =
    'w-full rounded-lg border border-slate-300 dark:border-border/80 bg-white dark:bg-background px-3 py-2 text-sm font-medium text-slate-900 dark:text-foreground focus:outline-none focus:ring-2 focus:ring-primary'

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="w-full max-w-2xl my-8 rounded-2xl border border-slate-200 dark:border-border/80 bg-white dark:bg-card shadow-2xl animate-in fade-in zoom-in-95 duration-200">

        {/* ── Header ── */}
        <div className="flex items-start justify-between p-6 border-b border-slate-200 dark:border-border/60">
          <div className="flex items-start gap-4">
            <div
              className={`flex size-11 shrink-0 items-center justify-center rounded-xl ${
                isApprove
                  ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-500'
                  : 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-500'
              }`}
            >
              {isApprove ? <CheckCircle2 className="size-6" /> : <XCircle className="size-6" />}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display text-lg font-bold text-slate-900 dark:text-foreground">
                  {isApprove ? 'Approve Account Request' : 'Reject Account Request'}
                </h3>
                <Badge variant="outline" className="text-[10px] font-mono uppercase bg-slate-50 dark:bg-background">
                  Form v{formVersion} Locked
                </Badge>
              </div>
              <p className="text-xs text-slate-500 dark:text-muted-foreground mt-0.5">
                Applicant:{' '}
                <span className="font-bold text-slate-800 dark:text-foreground">
                  {request.user?.firstName} {request.user?.lastName}
                </span>{' '}
                <span className="text-primary">({request.user?.email})</span>
              </p>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose} className="shrink-0">
            <XCircle className="size-4" />
          </Button>
        </div>

        <div className="p-6 space-y-5">

          {/* ── Application Answers ── */}
          {appDataEntries.length > 0 && (
            <div className="rounded-xl border border-slate-200 dark:border-border/80 bg-slate-50 dark:bg-background/50 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-border/60 bg-white dark:bg-card">
                <ClipboardList className="size-4 text-primary" />
                <span className="font-bold text-slate-800 dark:text-foreground text-sm">
                  Submitted Application Fields
                </span>
                <Badge variant="outline" className="ml-auto text-[10px]">
                  {appDataEntries.length} fields
                </Badge>
              </div>
              <div className="grid gap-0 divide-y divide-slate-100 dark:divide-border/40">
                {appDataEntries.map(([k, v], idx) => (
                  <div
                    key={k}
                    className={`flex items-start gap-3 px-4 py-3 ${
                      idx % 2 === 0
                        ? 'bg-white dark:bg-background/30'
                        : 'bg-slate-50/80 dark:bg-background/10'
                    }`}
                  >
                    <Hash className="size-3.5 text-slate-400 dark:text-muted-foreground shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold text-slate-500 dark:text-muted-foreground uppercase tracking-wide">
                        {getFieldLabel(k)}
                      </p>
                      <p className="font-semibold text-slate-900 dark:text-foreground text-sm mt-0.5 break-words">
                        {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Uploaded Documents ── */}
          {appDocs.length > 0 && (
            <div className="rounded-xl border border-slate-200 dark:border-border/80 bg-slate-50 dark:bg-background/50 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-border/60 bg-white dark:bg-card">
                <Download className="size-4 text-emerald-600 dark:text-emerald-500" />
                <span className="font-bold text-slate-800 dark:text-foreground text-sm">
                  Uploaded Application Documents
                </span>
                <Badge variant="outline" className="ml-auto text-[10px]">
                  {appDocs.length} file{appDocs.length > 1 ? 's' : ''}
                </Badge>
              </div>
              <div className="divide-y divide-slate-100 dark:divide-border/40">
                {appDocs.map((doc: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-3 px-4 py-3">
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-rose-50 dark:bg-rose-500/10">
                      <FileText className="size-4 text-rose-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 dark:text-foreground text-sm truncate">
                        {doc.fileName}
                      </p>
                      <p className="text-[11px] text-slate-500 dark:text-muted-foreground">
                        {doc.field && <span className="capitalize">{humanize(doc.field)} · </span>}
                        {doc.uploadedAt && new Date(doc.uploadedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className="text-[10px] font-mono">
                        v{doc.version || 1}
                      </Badge>
                      {doc.url && (
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-slate-200 dark:border-border/60 text-[11px] font-bold text-primary hover:bg-primary/5 transition-colors"
                        >
                          <Download className="size-3" /> View
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Applicant Remarks ── */}
          {remarks && (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-4">
              <MessageSquare className="size-4 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-[11px] font-bold text-amber-700 dark:text-amber-500 uppercase tracking-wide mb-1">
                  Applicant Remarks
                </p>
                <p className="text-sm text-slate-700 dark:text-foreground/90 italic leading-relaxed">
                  "{remarks}"
                </p>
              </div>
            </div>
          )}

          {/* ── Admin Action Form ── */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {isApprove && (
              <>
                {/* Plan Assignment */}
                <div className="rounded-xl border border-slate-200 dark:border-border/80 bg-slate-50 dark:bg-background/50 p-4 space-y-3">
                  <label className="font-bold text-slate-800 dark:text-foreground flex items-center gap-1.5 text-sm">
                    <Crown className="size-4 text-amber-500" /> Subscription Policy Assignment
                  </label>
                  <select
                    value={assignmentType}
                    onChange={(e) => setAssignmentType(e.target.value as any)}
                    className={selectCls}
                  >
                    <option value="REQUIRE_PAYMENT">Require Payment Before Activation (Customer Chooses Plan)</option>
                    <option value="DEFAULT">Assign Default Plan (Auto Active)</option>
                    <option value="SPECIFIC">Assign Specific Plan Directly</option>
                    <option value="TRIAL">Assign Free Trial Membership</option>
                    <option value="NONE">No Plan Assignment</option>
                  </select>

                  {assignmentType === 'SPECIFIC' && (
                    <div>
                      <label className="text-xs font-semibold text-slate-600 dark:text-muted-foreground">
                        Select Target Plan
                      </label>
                      <select
                        value={selectedPlanId ?? ''}
                        onChange={(e) => setSelectedPlanId(Number(e.target.value))}
                        className={`${selectCls} mt-1`}
                      >
                        <option value="">-- Choose Plan --</option>
                        {filteredPlans.map((plan: any) => (
                          <option key={plan.id} value={plan.id}>
                            {plan.name} — ₹{plan.price} ({plan.billingCycle})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {assignmentType === 'TRIAL' && (
                    <div>
                      <label className="text-xs font-semibold text-slate-600 dark:text-muted-foreground flex items-center gap-1">
                        <Clock className="size-3 text-primary" /> Custom Trial Duration (Days)
                      </label>
                      <Input
                        type="number"
                        value={customTrialDays}
                        onChange={(e) => setCustomTrialDays(Number(e.target.value))}
                        className="mt-1 text-sm"
                        min={1}
                        max={365}
                      />
                    </div>
                  )}
                </div>

                {/* Risk Level */}
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-slate-600 dark:text-muted-foreground flex items-center gap-1.5">
                    <ShieldCheck className="size-4 text-primary" /> Risk Level Assessment
                  </label>
                  <select
                    value={riskLevel}
                    onChange={(e) => setRiskLevel(e.target.value as 'LOW' | 'MEDIUM' | 'HIGH')}
                    className={`${selectCls} ${
                      riskLevel === 'HIGH'
                        ? 'border-red-300 dark:border-red-500/40'
                        : riskLevel === 'MEDIUM'
                        ? 'border-amber-300 dark:border-amber-500/40'
                        : 'border-emerald-300 dark:border-emerald-500/40'
                    }`}
                  >
                    <option value="LOW">🟢 Low Risk — Standard Approval</option>
                    <option value="MEDIUM">🟡 Medium Risk — Requires Periodic Audit</option>
                    <option value="HIGH">🔴 High Risk — High Volume Supervision</option>
                  </select>
                </div>
              </>
            )}

            {/* Notes */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-slate-600 dark:text-muted-foreground">
                {isApprove ? 'Reviewer Notes (Optional)' : 'Reason for Rejection'}
                {!isApprove && <span className="text-red-500 ml-1">*</span>}
              </label>
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={
                  isApprove
                    ? 'Add internal verification notes...'
                    : 'Specify why this application was rejected (e.g. invalid document, missing info)...'
                }
                className="w-full rounded-lg border border-slate-300 dark:border-border/80 bg-white dark:bg-background text-slate-900 dark:text-foreground placeholder:text-slate-400 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            {!isApprove && (
              <div className="flex items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
                <AlertTriangle className="size-4 shrink-0" />
                <span>User status will be set to REJECTED. User can edit and resubmit their application.</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-1 border-t border-slate-200 dark:border-border/60">
              <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isPending}
                className={
                  isApprove
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-sm'
                    : 'bg-red-600 hover:bg-red-700 text-white font-bold shadow-sm'
                }
              >
                {isPending
                  ? 'Processing...'
                  : isApprove
                  ? 'Confirm Approval & Assign'
                  : 'Confirm Rejection'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
